import { useRef, useState } from 'react';
import type { Payment, RefundReason } from '@salonomy/types';
import { Button, Field, Modal, Pill, SegmentedControl, Select, TextInput } from '@salonomy/ui';
import { useCreateRefund } from '../../data/payments.js';
import { useAuth } from '../../lib/auth.js';
import { ApiClientError } from '../../lib/api.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { penceToPounds, poundsToPence } from '../till/money.js';
import { PAYMENT_KIND_LABELS } from './paymentLabels.js';
import { REFUND_REASON_LABELS, REFUND_REASON_ORDER } from './refundReasons.js';

type RefundMode = 'full' | 'partial';

/**
 * WS6 — refund a payment, FULL or PARTIAL (manager-only on the API). MONEY HONESTY: the refundable amount
 * is the SERVER's `amountPence − amountRefundedPence` read straight off the Payment DTO — never recomputed
 * as authoritative here (we only subtract two server numbers to show the remaining cap, and the server
 * re-checks it). IDEMPOTENCY: the key is minted ONCE per refund intent and the submit button is gated on
 * `isPending`, so a double-tap REPLAYS the original refund and never re-charges; the key is never reused
 * across a different payment.
 *
 * DEPOSIT REFUND vs DEPOSIT FORFEIT: this is a normal refund. When the payment's `kind` is `deposit`, this
 * refunds the deposit back to the client. That is NOT the same as a deposit FORFEIT — forfeiting a deposit
 * on a no-show is the no-show-fee path (NoShowChargeModal), which charges, never refunds. The two must
 * never be confused or money moves the wrong way.
 *
 * RESTOCK: a PARTIAL refund does NOT restock product lines; only a FULL refund restocks. The form warns
 * the manager of this so a partial refund of a retail sale doesn't silently leave stock out of sync.
 */
export function RefundModal({ payment, onClose }: { payment: Payment; onClose: () => void }) {
  const toast = useToast();
  const { isManager } = useAuth();
  const refund = useCreateRefund();
  // Mint the idempotency key ONCE per refund intent (stable across re-renders, scoped to this payment).
  // A synchronous double-tap then carries the SAME key, so the server REPLAYS the original refund rather
  // than issuing a second one — `isPending`/`disabled` only flip on the next render, so the key (not the
  // button) is what makes a same-task double-fire safe. Never reused across a different payment.
  const idemKey = useRef(crypto.randomUUID()).current;

  // Both numbers are the server's (off the Payment DTO). We only subtract them to show the remaining cap;
  // the server re-validates the requested amount against the live balance under a row lock.
  const remaining = Math.max(0, payment.amountPence - payment.amountRefundedPence);

  const [mode, setMode] = useState<RefundMode>('full');
  const [amount, setAmount] = useState(penceToPounds(remaining));
  const [reason, setReason] = useState<RefundReason>('requested_by_customer');

  const parsed = poundsToPence(amount);
  const partialValid = parsed != null && parsed > 0 && parsed <= remaining;
  // FULL omits amountPence (the server refunds the whole remaining balance); PARTIAL sends the parsed pence.
  const canSubmit =
    isManager && remaining > 0 && (mode === 'full' || partialValid) && !refund.isPending;

  async function submit() {
    if (!canSubmit) return;
    try {
      // The idempotency key is minted ONCE per intent (above) — a double-tap reuses it, so the API
      // REPLAYS the original refund and never re-charges (idempotent per-payment). FULL ⇒ omit amount.
      await refund.mutateAsync({
        paymentId: payment.id,
        amountPence: mode === 'partial' ? (parsed ?? undefined) : undefined,
        reason,
        idempotencyKey: idemKey,
      });
      toast.success(
        mode === 'full' ? `Refunded ${money(remaining)} in full` : `Refunded ${money(parsed ?? 0)}`,
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not issue the refund');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={payment.kind === 'deposit' ? 'Refund deposit' : 'Refund payment'}
      width={460}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={submit} disabled={!canSubmit}>
            {refund.isPending
              ? 'Refunding…'
              : mode === 'full'
                ? `Refund ${money(remaining)}`
                : `Refund ${partialValid ? money(parsed ?? 0) : ''}`}
          </Button>
        </>
      }
    >
      <div className="till__line muted" style={{ marginBottom: 6 }}>
        <span>{PAYMENT_KIND_LABELS[payment.kind]} · charged</span>
        <span className="mono">{money(payment.amountPence)}</span>
      </div>
      {payment.amountRefundedPence > 0 ? (
        <div className="till__line muted">
          <span>Already refunded</span>
          <span className="mono">− {money(payment.amountRefundedPence)}</span>
        </div>
      ) : null}
      <div className="till__line till__line--total" style={{ marginBottom: 12 }}>
        <span>Refundable now</span>
        <span className="mono">{money(remaining)}</span>
      </div>

      {!isManager ? (
        <div style={{ marginBottom: 12 }}>
          <Pill tone="danger">Manager only</Pill>
          <div className="muted" style={{ marginTop: 8, fontSize: 13.5 }}>
            Refunds are a manager action. Ask an owner or manager to issue this refund.
          </div>
        </div>
      ) : remaining <= 0 ? (
        <div className="muted" style={{ fontSize: 13.5, marginBottom: 12 }}>
          This payment is fully refunded — there&rsquo;s nothing left to refund.
        </div>
      ) : (
        <>
          <Field label="Refund">
            <SegmentedControl<RefundMode>
              options={[
                { value: 'full', label: `Full · ${money(remaining)}` },
                { value: 'partial', label: 'Partial' },
              ]}
              value={mode}
              onChange={setMode}
            />
          </Field>

          {mode === 'partial' ? (
            <Field
              label="Amount to refund (£)"
              hint={`Up to ${money(remaining)}.`}
              error={
                parsed != null && parsed > remaining
                  ? `Can’t refund more than ${money(remaining)}.`
                  : null
              }
            >
              <TextInput
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </Field>
          ) : null}

          <Field label="Reason">
            <Select value={reason} onChange={(e) => setReason(e.target.value as RefundReason)}>
              {REFUND_REASON_ORDER.map((code) => (
                <option key={code} value={code}>
                  {REFUND_REASON_LABELS[code]}
                </option>
              ))}
            </Select>
          </Field>

          <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 4 }}>
            {mode === 'partial' ? (
              <>
                A <strong>partial</strong> refund does <strong>not</strong> restock any product
                lines — only a <strong>full</strong> refund restocks. The exact amount is settled by
                the payments server; every figure here is the server&rsquo;s, not recomputed.
              </>
            ) : (
              <>
                A <strong>full</strong> refund returns the whole remaining balance and restocks any
                product lines. Every figure here is the server&rsquo;s, not recomputed.
              </>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
